import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { Mail, CheckCircle, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';
  const role = location.state?.role || '';
  
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/email-verification/verify`, {
        email,
        code
      });

      if (response.data.email_verified) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Email vérifié avec succès ! Vous pouvez maintenant vous connecter.',
              email,
              role
            } 
          });
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Code de vérification invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/email-verification/resend-code`, { email });
      setError('');
      alert('Un nouveau code a été envoyé à votre adresse email');
    } catch (err) {
      setError('Erreur lors de l\'envoi du code');
    } finally {
      setResending(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#111111] p-4">
        <Card className="w-full max-w-md bg-[#1a1a1a] border-[#A6A6A6]/30">
          <CardContent className="pt-6">
            <div className="text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Email Vérifié !</h2>
              <p className="text-[#A6A6A6]">Redirection vers la page de connexion...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#111111] p-4">
      <Card className="w-full max-w-md bg-[#1a1a1a] border-[#A6A6A6]/30">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <Mail className="w-16 h-16 text-[#C00000]" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">
            Vérifiez votre email
          </CardTitle>
          <CardDescription className="text-[#A6A6A6]">
            Nous avons envoyé un code à 6 chiffres à <br/>
            <span className="text-white font-semibold">{email}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleVerify} className="space-y-6">
            <div>
              <Label htmlFor="code" className="text-[#A6A6A6]">
                Code de vérification
              </Label>
              <Input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                maxLength={6}
                required
                className="bg-[#111111] border-[#A6A6A6]/30 text-white text-center text-2xl tracking-widest focus:border-[#C00000]"
              />
              <p className="text-xs text-[#A6A6A6] mt-2">
                Entrez le code à 6 chiffres reçu par email
              </p>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full bg-[#C00000] hover:bg-[#A00000] text-white"
            >
              {loading ? 'Vérification...' : 'Vérifier'}
            </Button>

            <div className="text-center space-y-2">
              <p className="text-[#A6A6A6] text-sm">
                Vous n'avez pas reçu le code ?
              </p>
              <Button
                type="button"
                variant="ghost"
                onClick={handleResendCode}
                disabled={resending}
                className="text-[#C00000] hover:text-[#A00000] hover:bg-[#C00000]/10"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${resending ? 'animate-spin' : ''}`} />
                {resending ? 'Envoi en cours...' : 'Renvoyer le code'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
